import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Check, ChevronsUpDown, Plus } from "lucide-react";

export const CUSTOMER_ADMIN = ["management", "support", "office"];
const BLANK = { name: "", contact_person: "", email: "", phone: "", address: "", notes: "" };

export function CustomerForm({ open, onOpenChange, initial, onSaved }) {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ ...BLANK, ...(initial || {}) }); }, [open, initial]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.keys(BLANK).map((k) => [k, f[k] || ""]));
      const { data } = initial?.id ? await api.patch(`/customers/${initial.id}`, body) : await api.post("/customers", body);
      toast.success(initial?.id ? "Customer updated" : `Customer ${data.name} added`);
      onOpenChange(false); onSaved?.(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const fld = (k, label, props = {}) => (
    <div className={props.full ? "col-span-2" : "col-span-2 sm:col-span-1"}>
      <Label className="text-xs">{label}</Label>
      {props.area ? <Textarea rows={2} value={f[k]} onChange={set(k)} data-testid={`customer-form-${k}`} className="mt-1" />
        : <Input type={props.type || "text"} value={f[k]} onChange={set(k)} data-testid={`customer-form-${k}`} className="mt-1" />}
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="customer-form-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase">{initial?.id ? "Edit Customer" : "New Customer"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {fld("name", "Customer name *", { full: true })}
          {fld("contact_person", "Contact person")}
          {fld("phone", "Phone")}
          {fld("email", "Email", { type: "email", full: true })}
          {fld("address", "Address", { area: true, full: true })}
          {fld("notes", "Notes", { area: true, full: true })}
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.name.trim() || busy} data-testid="customer-form-save" className="font-bold uppercase">Save Customer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomerPicker({ value, onChange, testid = "customer-picker" }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  useEffect(() => { api.get("/customers").then((r) => setRows(r.data)).catch(() => {}); }, []);
  const picked = rows.find((c) => c.id === value);
  const canAdd = CUSTOMER_ADMIN.includes(user.role);
  const added = (c) => { setRows((s) => [...s, c].sort((a, b) => a.name.localeCompare(b.name))); onChange(c); };
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" data-testid={testid} className="w-full justify-between font-normal">
            <span className={picked ? "" : "text-muted-foreground"}>{picked ? picked.name : "Select customer…"}</span>
            <ChevronsUpDown className="w-4 h-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
          <Command>
            <CommandInput placeholder="Search customers…" value={search} onValueChange={setSearch} data-testid={`${testid}-search`} />
            <CommandList>
              <CommandEmpty>No customer found.</CommandEmpty>
              <CommandGroup>
                {rows.map((c) => (
                  <CommandItem key={c.id} value={`${c.name} ${c.id}`} onSelect={() => { onChange(c); setOpen(false); }} data-testid={`${testid}-option-${c.id}`}>
                    <Check className={`w-4 h-4 mr-2 ${c.id === value ? "opacity-100" : "opacity-0"}`} />
                    <span className="truncate">{c.name}</span>
                    {c.contact_person && <span className="ml-auto text-xs text-muted-foreground truncate">{c.contact_person}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
            {canAdd && (
              <button type="button" onClick={() => { setOpen(false); setAdding(true); }} data-testid={`${testid}-add-new`}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-primary border-t border-border hover:bg-accent transition-colors">
                <Plus className="w-4 h-4" />Add new customer{search ? ` "${search}"` : ""}
              </button>
            )}
          </Command>
        </PopoverContent>
      </Popover>
      <CustomerForm open={adding} onOpenChange={setAdding} initial={search ? { name: search } : null} onSaved={added} />
    </>
  );
}
